import { useState } from 'react';

interface HeroProps {
  language: 'pt' | 'en';
  onOpenForm: () => void;
}

export default function Hero({ language, onOpenForm }: HeroProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const content = {
    pt: {
      eyebrow: 'Engenharia Patrimonial',
      title: 'Diagnóstico Estratégico de Engenharia Patrimonial',
      subtitle: 'Decisão com método, governança e execução no sistema de consórcios regulados.',
      cta1: 'Agendar Diagnóstico',
      cta2: 'WhatsApp: Falar com Especialista',
      forTitle: 'Indicado Para',
      items: [
        {
          label: 'Aquisição imobiliária (moradia ou investimento)',
          card: {
            title: 'Aquisição Imobiliária',
            description: 'Utilize o consórcio como instrumento de planejamento para adquirir imóvel residencial ou de investimento sem juros, com parcelas previsíveis e prazo definido.',
            highlight: 'Sem juros. Sem entrada obrigatória.'
          }
        },
        {
          label: 'Construção e reforma com planejamento de fluxo',
          card: {
            title: 'Construção e Reforma',
            description: 'Estruture o financiamento da sua obra com fluxo de caixa disciplinado. O consórcio permite planejar etapas de construção ou reforma com previsibilidade total.',
            highlight: 'Controle total do fluxo financeiro.'
          }
        },
        {
          label: 'Crédito Contemplado de curto prazo',
          card: {
            title: 'Crédito Contemplado',
            description: 'Acesso imediato a crédito por meio de carta contemplada já disponível no mercado. Solução ágil para quem precisa de liquidez no curto prazo com custo efetivo menor que o crédito tradicional.',
            highlight: 'Liquidez imediata com custo reduzido.'
          }
        },
        {
          label: 'Expansão patrimonial no médio e longo prazo',
          card: {
            title: 'Expansão Patrimonial',
            description: 'Estratégia de acumulação de ativos reais ao longo do tempo. O consórcio funciona como um veículo disciplinado de formação de patrimônio com proteção contra inflação.',
            highlight: 'Patrimônio real com disciplina financeira.'
          }
        },
        {
          label: 'Avaliação técnica de carta contemplada',
          card: {
            title: 'Avaliação de Carta Contemplada',
            description: 'Análise criteriosa do valor de mercado, grupo, administradora e riscos de uma carta contemplada antes da aquisição. Evite armadilhas e maximize o retorno do seu investimento.',
            highlight: 'Due diligence especializada antes de comprar.'
          }
        }
      ]
    },
    en: {
      eyebrow: 'Wealth Engineering',
      title: 'Strategic Assessment for Structured Wealth Engineering',
      subtitle: 'Your decision with method, governance and disciplined execution within regulated consortium frameworks.',
      cta1: 'Schedule Strategic Assessment',
      cta2: 'WhatsApp: Speak with Specialist',
      forTitle: 'Suitable For',
      items: [
        {
          label: 'Real estate acquisition (residence or investment)',
          card: {
            title: 'Real Estate Acquisition',
            description: 'Use the consortium as a planning instrument to acquire residential or investment property without interest, with predictable installments and a defined term.',
            highlight: 'No interest. No mandatory down payment.'
          }
        },
        {
          label: 'Construction and renovation with cash flow planning',
          card: {
            title: 'Construction and Renovation',
            description: 'Structure your project financing with disciplined cash flow. The consortium allows you to plan construction or renovation stages with full predictability.',
            highlight: 'Full control of financial flow.'
          }
        },
        {
          label: 'Short-term Awarded Credit',
          card: {
            title: 'Awarded Credit',
            description: 'Immediate access to credit through an awarded letter already available on the market. An agile solution for those who need short-term liquidity at a lower effective cost than traditional credit.',
            highlight: 'Immediate liquidity at reduced cost.'
          }
        },
        {
          label: 'Medium and long-term wealth expansion',
          card: {
            title: 'Wealth Expansion',
            description: 'Strategy for accumulating real assets over time. The consortium works as a disciplined vehicle for wealth building with inflation protection.',
            highlight: 'Real assets with financial discipline.'
          }
        },
        {
          label: 'Technical evaluation of awarded letter',
          card: {
            title: 'Awarded Letter Evaluation',
            description: 'Careful analysis of market value, group, administrator and risks of an awarded letter before acquisition. Avoid pitfalls and maximize your investment return.',
            highlight: 'Specialized due diligence before buying.'
          }
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section className="relative bg-[#FBF8F3] border-b border-[#E7E1D9] overflow-hidden">
      {/* Decorative image — right half, grayscale, zoomed */}
      <div className="absolute top-0 right-0 w-[58%] h-full pointer-events-none select-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/12b998569ed150abf6b0599479b6af9d.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{
            filter: 'grayscale(100%) contrast(1.3)',
            opacity: 0.26,
            transform: 'scale(1.5)',
            transformOrigin: 'center top',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="max-w-4xl">

          {/* Eyebrow editorial */}
          <div className="flex items-center gap-4 mb-14">
            <span className="text-sm uppercase tracking-widest text-[#2E2E2E] font-semibold">
              {t.eyebrow}
            </span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-semibold text-[#0B0B0B] leading-tight mb-8">
            {t.title}
          </h1>
          <p className="text-xl lg:text-2xl text-[#2E2E2E] leading-relaxed mb-16 max-w-3xl">
            {t.subtitle}
          </p>

          {/* Indicado Para */}
          <div className="border-t border-[#E7E1D9] pt-12">
            <h2 className="text-sm uppercase tracking-widest text-[#2E2E2E] mb-8 font-semibold">
              {t.forTitle}
            </h2>
            <ul className="space-y-3">
              {t.items.map((item, index) => (
                <li
                  key={index}
                  className="relative group"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {/* Item row */}
                  <div
                    className={`flex items-center gap-4 px-4 py-3 rounded-lg cursor-default transition-all duration-200 ${
                      activeIndex === index
                        ? 'bg-[#F97700]/8 border border-[#F97700]/20'
                        : 'border border-transparent hover:bg-[#F5F0E8]'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-200 ${
                        activeIndex === index ? 'bg-[#F97700] scale-125' : 'bg-[#F97700]'
                      }`}
                    />
                    <span
                      className={`text-lg transition-colors duration-200 ${
                        activeIndex === index ? 'text-[#F97700] font-medium' : 'text-[#0B0B0B]'
                      }`}
                    >
                      {item.label}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenForm(); }}
                      className={`ml-auto flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeIndex === index
                          ? 'text-white bg-[#F97700] border-[#F97700] opacity-100 scale-100'
                          : 'opacity-0 scale-95 pointer-events-none text-[#F97700] bg-transparent border-[#F97700]'
                      }`}
                    >
                      <span>{language === 'pt' ? 'Saiba Mais' : 'Learn More'}</span>
                      <i className="ri-arrow-right-line text-xs" />
                    </button>
                  </div>

                  {/* Card explicativo */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      activeIndex === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="mx-4 mb-2 p-5 bg-white border border-[#E7E1D9] rounded-lg shadow-sm">
                      <div className="flex items-start gap-4">
                        <div className="w-1 h-full min-h-[60px] bg-[#F97700] rounded-full flex-shrink-0 self-stretch" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-[#0B0B0B] uppercase tracking-wide mb-2">
                            {item.card.title}
                          </h3>
                          <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">
                            {item.card.description}
                          </p>
                          <span className="inline-block text-xs font-semibold text-[#F97700] bg-[#F97700]/10 px-3 py-1 rounded-full">
                            {item.card.highlight}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
